import React, { useCallback, useEffect, useRef } from 'react';
import { common, userInfo } from '@/common';
import { MAGIC_HOST_CONTEXT, MAGIC_READY, resolveStoreMarketingUrl } from './config';
import './index.less';

export {
  STORE_MARKETING_URL,
  MAGIC_HOST_CONTEXT,
  MAGIC_READY,
} from './config';

/** 下发给门店AI营销页面的上下文（MAGIC_HOST_CONTEXT.payload） */
export interface MagicHostContext {
  /** 必填 */
  token: string;
  /** 必填 —— 商户 ID */
  orgId: string;
  /** 可选 —— 服务商 code，仅作服务商校验 */
  tenantCode: string;
  /** 必填 */
  storeId: string;
  language: string;
  timezone: string;
  currency: string;
  roles: string;
  bizType: string;
  solutionCode: string;
  hostSource: string;
  uid: string;
  email: string;
  phone: string;
}

/**
 * 解析目标 origin。
 *
 * payload 里带 token，所以解析不出来时返回 null 并放弃下发，
 * 绝不退化成 '*' —— 那等于把 token 广播给任意源。
 */
function resolveTargetOrigin(url: string): string | null {
  try {
    return new URL(url, window.location.href).origin;
  } catch {
    return null;
  }
}

/** 宿主未下发 timezone 时回退到浏览器时区 */
function resolveTimezone(): string {
  if (common.timezone) return common.timezone;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

function buildContext(): MagicHostContext {
  return {
    token: common.token ?? '',
    orgId: common.orgId ?? '',
    tenantCode: common.tenantCode ?? '',
    storeId: common.storeId ?? '',
    language: common.language ?? 'en-US',
    timezone: resolveTimezone(),
    currency: common.currency ?? '',
    roles: common.roles ?? '',
    bizType: common.bizType ?? '',
    solutionCode: String(common.solution_code ?? ''),
    hostSource: String(common.source ?? ''),
    uid: userInfo.uid ?? '',
    email: userInfo.email ?? '',
    phone: userInfo.phone ?? '',
  };
}

/**
 * StoreMarketing — 门店AI营销 iframe 容器
 *
 * 下发报文：`{ type: 'MAGIC_HOST_CONTEXT', payload }`
 *
 * 时序上一共下发两次：
 * 1. iframe load 事件后立即发一次（子页面已注册监听时即可收到）
 * 2. 收到子页面 `{ type: 'MAGIC_READY' }` 后再发一次（覆盖监听注册晚于第 1 次的情况）
 *
 * MAGIC_READY 只响应第一次：若子页面把它当成每条 MAGIC_HOST_CONTEXT 的回执，
 * 重复响应会形成 context → ready → context 的无限互相触发。
 *
 * `visible` 为 false 时只隐藏不卸载，避免切走再切回时营销页整个重载。
 */
const StoreMarketing: React.FC<{ visible?: boolean }> = ({ visible = true }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const readyHandledRef = useRef(false);
  const marketingUrl = resolveStoreMarketingUrl();
  const targetOrigin = resolveTargetOrigin(marketingUrl);

  const postContext = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame?.contentWindow) return;

    if (!targetOrigin) {
      console.error('[StoreMarketing] 目标 origin 解析失败，已放弃下发：', marketingUrl);
      return;
    }

    frame.contentWindow.postMessage(
      {
        type: MAGIC_HOST_CONTEXT,
        payload: buildContext(),
      },
      targetOrigin,
    );

    // 营销页通常跨域，控制台读不到它的内容，这里把下发内容打出来便于联调
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[StoreMarketing] ${MAGIC_HOST_CONTEXT} → ${targetOrigin}`, buildContext());
    }
  }, [targetOrigin, marketingUrl]);

  /** 第 2 次下发：子页面首次 MAGIC_READY */
  useEffect(() => {
    if (!targetOrigin) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== targetOrigin) return;

      const frame = iframeRef.current;
      if (frame?.contentWindow && event.source !== frame.contentWindow) return;

      const data = event.data as { type?: string } | undefined;
      if (data?.type !== MAGIC_READY) return;

      if (readyHandledRef.current) return;
      readyHandledRef.current = true;

      postContext();
    };

    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [targetOrigin, postContext]);

  return (
    /**
     * 外层容器承载显示/隐藏，iframe 本身始终不被卸载：
     * 卸载会导致整篇文档重载，营销页会重放初始化、丢掉历史滚动位置、
     * 并中断进行中的对话。用 display:none 隐藏则文档保持存活。
     *
     * display:none 期间 iframe 的视口尺寸会坍缩为 0，重新显示时若营销页
     * 依赖自身内部的 resize 监听重排，浏览器会派发 resize —— 但仍建议
     * 在 P0 联调时确认一次卡片布局正常。
     */
    <div className={`store-marketing${visible ? '' : ' store-marketing--hidden'}`}>
      <iframe
        ref={iframeRef}
        className="store-marketing__frame"
        src={marketingUrl}
        title="Store AI Marketing"
        /** 第 1 次下发 */
        onLoad={postContext}
        allow="clipboard-read; clipboard-write"
      />
    </div>
  );
};

export default StoreMarketing;
